import { analyzeKeywords } from './keywords';
import { buildRewrites } from './rewrite';
import { RULES } from './rules';
import {
  SECTION_LABELS,
  SECTION_WEIGHTS,
  clamp01,
  severityRank,
  toGrade,
  weightedMean,
} from './scoring';
import {
  SECTION_IDS,
  type AudienceMode,
  type Finding,
  type ProfileChecklist,
  type ProfileInput,
  type Report,
  type RuleContext,
  type SectionReport,
} from './types';

export interface AnalyzeOptions {
  /**
   * ISO timestamp to stamp on the report. Injected rather than read from the
   * clock so `analyze` is a pure function and its output is snapshot-testable.
   */
  now?: string;
}

/** A blank profile. Used to seed the form and as a baseline in tests. */
export function createEmptyProfile(): ProfileInput {
  return {
    headline: '',
    about: '',
    experience: [],
    skills: [],
    targetRole: '',
    checklist: createEmptyChecklist(),
  };
}

export function createEmptyChecklist(): ProfileChecklist {
  return {
    hasPhoto: false,
    hasBanner: false,
    hasCustomUrl: false,
    hasLocation: false,
    hasFeatured: false,
    recommendationCount: 0,
    openToWork: false,
    hiring: false,
  };
}

/**
 * Runs the full rule set over a profile and returns a scored report.
 *
 * Pure: same input, same output. All I/O (the clock, storage, the network) is
 * the caller's problem. That is what makes the engine testable and what lets it
 * run in a browser, in Node, or in a future CLI unchanged.
 */
export function analyze(
  profile: ProfileInput,
  mode: AudienceMode,
  options: AnalyzeOptions = {}
): Report {
  const keywords = analyzeKeywords(profile);
  const context: RuleContext = { profile, mode, keywords };
  const sectionWeights = SECTION_WEIGHTS[mode];

  const sections: SectionReport[] = SECTION_IDS.map((section) => {
    const applicable = RULES.filter(
      (rule) => rule.section === section && (!rule.appliesTo || rule.appliesTo.includes(mode))
    );

    const scored: { score: number; weight: number }[] = [];
    const findings: Finding[] = [];

    for (const rule of applicable) {
      const outcome = rule.evaluate(context);
      if (outcome.findings?.length) findings.push(...outcome.findings);
      // A skipped rule contributes neither score nor weight, so a question the
      // user was never asked can never cost them points.
      if (!outcome.skipped) {
        scored.push({ score: clamp01(outcome.score), weight: rule.weight });
      }
    }

    const mean = weightedMean(scored);

    return {
      section,
      label: SECTION_LABELS[section],
      score: mean === null ? null : Math.round(mean * 100),
      // Provisional: renormalised below once we know which sections scored.
      weight: sectionWeights[section] / 100,
      findings: sortFindings(findings),
    };
  });

  // Renormalise over the sections that actually produced a score, so a section
  // that was entirely skipped redistributes its weight instead of counting as 0.
  const graded = sections.filter(
    (section): section is SectionReport & { score: number } => section.score !== null
  );
  const gradedWeight = graded.reduce((sum, section) => sum + sectionWeights[section.section], 0);

  for (const section of sections) {
    section.weight =
      gradedWeight > 0 && section.score !== null
        ? sectionWeights[section.section] / gradedWeight
        : 0;
  }

  const overallMean = weightedMean(
    graded.map((section) => ({
      score: section.score,
      weight: sectionWeights[section.section],
    }))
  );
  const overall = Math.round(overallMean ?? 0);

  const findings = sortFindings(sections.flatMap((section) => section.findings));

  return {
    overall,
    grade: toGrade(overall),
    mode,
    sections,
    findings,
    topFixes: findings.slice(0, 3),
    keywords,
    rewrites: buildRewrites(context),
    generatedAt: options.now ?? '',
  };
}

/**
 * Worst first: severity, then the order the sections appear on a LinkedIn
 * profile, then id. The final tie-break on id keeps output deterministic, which
 * the snapshot tests depend on.
 */
export function sortFindings(findings: readonly Finding[]): Finding[] {
  return [...findings].sort((a, b) => {
    const bySeverity = severityRank(a.severity) - severityRank(b.severity);
    if (bySeverity !== 0) return bySeverity;
    const bySection = SECTION_IDS.indexOf(a.section) - SECTION_IDS.indexOf(b.section);
    if (bySection !== 0) return bySection;
    return a.id.localeCompare(b.id);
  });
}
